/**
 * LibraryLinkCta — the Files tab's door into the Library picker.
 *
 * Shown only to someone who has a Library: the feature resolves ON for them
 * (user override → tenant → default) AND they hold an admin role — the
 * Sidebar's own rule. The Library is closed to the plain `user` role (the
 * vault API refuses it), and anyone may create and so manage a project, so a
 * plain user would get a picker that cannot open (Segev, 2026-10-29). The links
 * already made stay listed for the project's managers, whatever their role;
 * only the door is gated.
 */

import { useAuth } from '@/hooks/useAuth';
import { useFeature } from '@/hooks/useFeature';
import { useTranslator } from '../hooks/useTranslator';

interface LibraryLinkCtaProps {
  onOpen: () => void;
}

export function LibraryLinkCta({ onOpen }: LibraryLinkCtaProps) {
  const { t } = useTranslator();
  const libraryOn = useFeature('modules.library');
  const { isAdmin } = useAuth();
  if (!(libraryOn && isAdmin)) return null;

  return (
    <div className="files-library-cta">
      <button
        type="button"
        className="pj-btn pj-btn-secondary pj-btn-sm"
        onClick={onOpen}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
          <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
        </svg>
        {t('projects.library.linkCta') || 'Link from Library'}
      </button>
      <span dir="auto" className="files-library-cta-hint">
        {t('projects.library.linkCtaHint')
          || 'Share a Library folder or file — its summary travels with it.'}
      </span>
    </div>
  );
}

export default LibraryLinkCta;
